import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from "../firebase"; 
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';

function AddProductPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // Use 'id' to match your edit route '/edit/:id'
  const isEditing = Boolean(id);
  const { hasPermission } = useAuth();

  // Form State
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState(''); // Add price state
  const [imageBase64, setImageBase64] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch categories for the dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Category meals'));
        const categoriesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setCategories(categoriesList);
      } catch (err) {
        setError('Failed to load categories.');
      }
    };
    fetchCategories();
  }, []);

  // Fetch product data if editing
  useEffect(() => {
    const fetchProduct = async () => {
      if (!isEditing) {
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, 'MealsProducts', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name || '');
          setQuantity(data.quantity || '');
          setPrice(data.price || ''); // Set price from fetched data
          setImageBase64(data.imageUrl || '');
          setSelectedCategory(data.categoryId || '');
        } else {
          setError('Product not found.');
        }
      } catch (err) {
        setError('Failed to load product data.');
      } finally {
        setLoading(false);
      }
    };
    if (categories.length > 0) {
        fetchProduct();
    } else if (!isEditing) {
        setLoading(false);
    }
  }, [id, isEditing, categories]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setImageBase64(reader.result);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing && !hasPermission('raw_products.update')) {
      return setError("You don't have permission to update products.");
    }
    if (!isEditing && !hasPermission('raw_products.create')) {
      return setError("You don't have permission to create products.");
    }
    if (!name || !selectedCategory) {
      setError('Product Name and Category are required.');
      return;
    }

    setLoading(true);
    const categoryObject = categories.find(c => c.id === selectedCategory);

    const productData = {
      name,
      quantity: Number(quantity) || 0,
      price: Number(price) || 0,
      imageUrl: imageBase64,
      categoryId: selectedCategory,
      categoryName: categoryObject ? categoryObject.name : '',
      updatedAt: serverTimestamp(),
    };

    try {
      if (isEditing) {
        const docRef = doc(db, 'MealsProducts', id);
        await updateDoc(docRef, productData);
        alert('Product updated successfully!');
      } else {
        productData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'MealsProducts'), productData);
        alert('Product created successfully!');
      }
      navigate('/product');
    } catch (err) {
      setError('Failed to save product.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
      <h2>{isEditing ? 'Edit Product' : 'Add New Product'}</h2>
      <form onSubmit={handleSubmit}>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        
        <div style={{ marginBottom: '1rem' }}>
          <label>Category</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} required style={{ width: '100%', padding: '10px' }}>
            <option value="">-- Select a Category --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Product Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required style={{ width: '100%', padding: '10px' }} />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Quantity</label>
          <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} required style={{ width: '100%', padding: '10px' }} />
        </div>
         <div style={{ marginBottom: '1rem' }}>
          <label>Price</label>
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required style={{ width: '100%', padding: '10px' }} />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Image</label>
          <input type="file" accept="image/*" onChange={handleImageChange} required={!isEditing} />
        </div>
        {imageBase64 && <img src={imageBase64} alt="Preview" style={{ maxWidth: '100px', height: 'auto', marginBottom: '1rem' }} />}
        
        <button type="submit" disabled={loading} style={{ padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px' }}>
          {loading ? 'Saving...' : (isEditing ? 'Update Product' : 'Add Product')}
        </button>
      </form>
    </div>
  );
}

export default AddProductPage;